/**
 * InvestorAuth.jsx — Investor Authentication Gate
 * GRANULLO_INVESTOR_CONVICTION_ROOM_V1
 * 
 * Private investor login. Premium, Bloomberg-terminal feel.
 * Invite-only registration. Session management.
 */
import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const InvestorAuthContext = createContext(null);

export function useInvestorAuth() {
  return useContext(InvestorAuthContext);
}

export function InvestorAuthProvider({ children }) {
  const [investor, setInvestor] = useState(null); // null = checking
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/investor-auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setInvestor(data);
      } else {
        setInvestor(false);
      }
    } catch {
      setInvestor(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/investor-auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(typeof err.detail === 'string' ? err.detail : 'Login failed');
    }
    const data = await res.json();
    setInvestor(data);
    return data;
  };

  const register = async (invite_token, email, password, name, firm) => {
    const res = await fetch(`${API}/api/investor-auth/register`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ invite_token, email, password, name, firm }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(typeof err.detail === 'string' ? err.detail : 'Registration failed');
    }
    const data = await res.json();
    setInvestor(data);
    return data;
  };

  const logout = async () => {
    await fetch(`${API}/api/investor-auth/logout`, { method: 'POST', credentials: 'include' });
    setInvestor(false);
  };

  const trackModule = async (module, action = 'view') => {
    try {
      await fetch(`${API}/api/investor-auth/track`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ module, action }),
      });
    } catch { /* silent */ }
  };

  return (
    <InvestorAuthContext.Provider value={{ investor, loading, login, register, logout, trackModule }}>
      {children}
    </InvestorAuthContext.Provider>
  );
}

export function InvestorLoginGate({ children }) {
  const { investor, loading } = useInvestorAuth();
  const [mode, setMode] = useState('login'); // login | register

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--shell-bg)' }}>
        <p className="text-[12px] font-mono" style={{ color: 'var(--shell-text-muted)' }}>Verifying investor session...</p>
      </div>
    );
  }

  if (investor) return children;

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--shell-bg)' }}
      data-testid="investor-login-gate">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[400px] px-6">
        <div className="text-center mb-8">
          <Lock size={20} className="mx-auto mb-4" style={{ color: 'var(--shell-text-muted)' }} />
          <p className="text-[10px] font-mono uppercase tracking-[0.16em] mb-2"
            style={{ color: 'var(--shell-text-muted)' }}>
            Private Investor Diligence Room
          </p>
          <h1 className="text-[28px] leading-[1.1] tracking-[-0.02em]"
            style={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: 'var(--shell-text)' }}>
            Granullo
          </h1>
        </div>
        {mode === 'login' ? (
          <InvestorLoginForm onSwitchToRegister={() => setMode('register')} />
        ) : (
          <InvestorRegisterForm onSwitchToLogin={() => setMode('login')} />
        )}
      </motion.div>
    </div>
  );
}

function InvestorLoginForm({ onSwitchToRegister }) {
  const { login } = useInvestorAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="investor-login-form">
      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-mono uppercase tracking-[0.06em] block mb-1"
            style={{ color: 'var(--shell-text-muted)' }}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            className="w-full p-3 rounded text-[14px]"
            style={{ background: 'var(--g-surface)', border: '1px solid var(--g-border)', color: 'var(--shell-text)', outline: 'none' }}
            data-testid="investor-login-email" required />
        </div>
        <div className="relative">
          <label className="text-[11px] font-mono uppercase tracking-[0.06em] block mb-1"
            style={{ color: 'var(--shell-text-muted)' }}>Password</label>
          <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
            className="w-full p-3 rounded text-[14px] pr-10"
            style={{ background: 'var(--g-surface)', border: '1px solid var(--g-border)', color: 'var(--shell-text)', outline: 'none' }}
            data-testid="investor-login-password" required />
          <button type="button" onClick={() => setShowPw(!showPw)}
            className="absolute right-3 top-8" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--shell-text-muted)' }}>
            {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>

      {error && <p className="text-[12px] mt-3" style={{ color: 'var(--shell-scarlet, #CD2200)' }} data-testid="investor-login-error">{error}</p>}

      <button type="submit" disabled={loading}
        className="w-full mt-6 py-3 rounded text-[13px] font-medium flex items-center justify-center gap-2"
        style={{ background: 'var(--shell-text)', color: 'var(--shell-bg)', border: 'none', cursor: 'pointer', opacity: loading ? 0.5 : 1 }}
        data-testid="investor-login-submit">
        {loading ? 'Verifying...' : 'Enter Conviction Room'} <ArrowRight size={14} />
      </button>

      <p className="text-[11px] text-center mt-4" style={{ color: 'var(--shell-text-muted)' }}>
        Have an invite?{' '}
        <button type="button" onClick={onSwitchToRegister}
          style={{ color: 'var(--shell-text)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          Register
        </button>
      </p>
    </form>
  );
}

function InvestorRegisterForm({ onSwitchToLogin }) {
  const { register } = useInvestorAuth();
  const [form, setForm] = useState({ invite_token: '', email: '', password: '', name: '', firm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form.invite_token, form.email, form.password, form.name, form.firm);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <form onSubmit={handleSubmit} data-testid="investor-register-form">
      <div className="space-y-3">
        <FormInput label="Invite Token" value={form.invite_token} onChange={v => update('invite_token', v)} testid="investor-reg-token" required />
        <FormInput label="Email" value={form.email} onChange={v => update('email', v)} type="email" testid="investor-reg-email" required />
        <FormInput label="Password" value={form.password} onChange={v => update('password', v)} type="password" testid="investor-reg-password" required />
        <FormInput label="Name" value={form.name} onChange={v => update('name', v)} testid="investor-reg-name" required />
        <FormInput label="Firm (optional)" value={form.firm} onChange={v => update('firm', v)} testid="investor-reg-firm" />
      </div>
      {error && <p className="text-[12px] mt-3" style={{ color: 'var(--shell-scarlet, #CD2200)' }}>{error}</p>}
      <button type="submit" disabled={loading}
        className="w-full mt-5 py-3 rounded text-[13px] font-medium flex items-center justify-center gap-2"
        style={{ background: 'var(--shell-text)', color: 'var(--shell-bg)', border: 'none', cursor: 'pointer', opacity: loading ? 0.5 : 1 }}
        data-testid="investor-register-submit">
        {loading ? 'Creating...' : 'Create Account'} <ArrowRight size={14} />
      </button>
      <p className="text-[11px] text-center mt-4" style={{ color: 'var(--shell-text-muted)' }}>
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin}
          style={{ color: 'var(--shell-text)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          Login
        </button>
      </p>
    </form>
  );
}

function FormInput({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-[0.06em] block mb-1"
        style={{ color: 'var(--shell-text-muted)' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required}
        className="w-full p-3 rounded text-[14px]"
        style={{ background: 'var(--g-surface)', border: '1px solid var(--g-border)', color: 'var(--shell-text)', outline: 'none' }}
        data-testid={testid} />
    </div>
  );
}
